import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Step from "@mui/material/Step";
import StepButton from "@mui/material/StepButton";
import Stepper from "@mui/material/Stepper";
import { useLocation } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import dayjs from "dayjs";

import { useStaticData } from "../../../context/StaticDataContext";
import { useAuth } from "../../../context/AuthContext";
import { RELATIONSHIP_CATEGORIES } from "../../../constants/constants";
import AppDatePicker from "../../components-ui/AppDatePicker";
import AddressCard from "./components/AddressCard";
import AgreementCard, {
  Agreement,
  createDefaultAgreements,
} from "./components/AgreementCard";
import ConsentCard, {
  Consent,
  createDefaultConsents,
} from "./components/ConsentCard";
import YouthProfileCard, {
  emptyYouthProfile,
  HouseholdMember,
  YouthProfile,
} from "./components/YouthProfileCard";
import EmergencyContactCard, {
  hasCompleteEmergencyContact,
} from "./components/EmergencyContactCard";
import ServiceEnrollmentCard from "./components/ServiceEnrollmentCard";
import {
  Address,
  PartyAddress,
  PersonDetails,
  RegistrationMemberType,
  RelationshipType,
  ServiceRegistrationRequirementDto,
  EmergencyContact,
} from "../../types/types";
import { api } from "../../../api/apiClient";
import { notify } from "../../components-ui/toast";

import "../../../css/forms.css";
import "../../../css/buttons.css";
import "../../../css/cards.css";
import "../../../css/tables.css";
import "./PartyFamily.css";
import { PartyFormMode, usePartyFormContext } from "./PartyFormContext";

type FamilyPerson = {
  id: string;
  partyId?: number;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: string;
  mobileNumber?: string;
  email?: string;
  relationshipTypeId?: number;
  emergencyContact?: EmergencyContact | null;
};

type FamilyRegistration = {
  primaryContact: FamilyPerson;
  secondaryContact: FamilyPerson;
  secondaryContactStaysAtSameAddress: boolean;
  household: {
    name?: string;
    notes?: string;
    household?: Address;
    partyAddress?: PartyAddress;
  };
  householdMembers: FamilyPerson[];
  relationships: {
    memberIndex: number;
    relationshipTypeId: number;
  }[];
  partyRelationships: FamilyContextRelationship[];
  youthProfiles: Record<string, YouthProfile>;
  consents: Record<string, Consent[]>;
  agreements: Record<string, Agreement[]>;
};

type FamilyContextRelationship = {
  partyRelationshipId: number;
  fromPartyId: number;
  fromPartyName: string;
  toPartyId: number;
  relationshipTypeId: number;
};

export type FamilyContextPerson = Omit<FamilyPerson, "id"> & {
  partyId?: number;
  youthProfile?: YouthProfile | null;
  consents?: Consent[];
  agreements?: Agreement[];
};

export type FamilyContext = {
  selectedPartyId: number;
  householdId?: number;
  primaryContact: FamilyContextPerson;
  secondaryContact?: FamilyContextPerson | null;
  secondaryContactStaysAtSameAddress?: boolean;
  household: {
    name?: string;
    notes?: string;
    household?: Address;
    partyAddress?: PartyAddress;
  };
  members: FamilyContextPerson[];
  relationships: {
    memberIndex: number;
    relationshipTypeId: number;
  }[];
  partyRelationships: FamilyContextRelationship[];
};


type FamilyRegistrationResult = {
  householdId: number;
  primaryContactPartyId: number;
  memberPartyIds: number[];
  serviceEnrollmentIds: number[];
};

const createEmptyPerson = (): FamilyPerson => ({
  id: "",
  firstName: "",
  lastName: "",
  dateOfBirth: "",
  gender: "",
  mobileNumber: "",
  email: "",
});

const steps = [
  "Primary Contact",
  "Secondary Contact",
  "Children",
  "Review",
];

const validMemberTypes: RegistrationMemberType[] = [
  "ServiceUser",
  "PrimaryContact",
  "Volunteer",
  "Staff",
];

const createMemberId = () =>
  `member-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const calculateAge = (dateOfBirth: string) => {
  if (!dateOfBirth) return null;

  const today = new Date();
  const dob = new Date(`${dateOfBirth}T00:00:00`);
  let age = today.getFullYear() - dob.getFullYear();
  const monthDelta = today.getMonth() - dob.getMonth();

  if (monthDelta < 0 || (monthDelta === 0 && today.getDate() < dob.getDate())) {
    age--;
  }

  return age;
};

const calculateAgeLabel = (dateOfBirth: string) => {
  const age = calculateAge(dateOfBirth);
  if (age === null) return "Adult";
  return age >= 18 ? "Adult" : String(age);
};

const getDisplayName = (person: { firstName: string; lastName: string }) =>
  `${person.firstName} ${person.lastName}`.trim() || "-";

interface PartyFamilyProps {
  partyId: number | null;
  initialStep?: number;
  onCompleted?: () => void;
  onCancel?: () => void;
  saveLabel?: string;
  cancelLabel?: string;
  initialContext?: FamilyContext;
  consentTemplates?: Consent[];
  agreementTemplates?: Agreement[];
}

const mapContextPerson = (
  person: FamilyContextPerson,
  fallbackId = "primary-contact",
): FamilyPerson => ({
  ...person,
  id: person.partyId ? String(person.partyId) : fallbackId,
  dateOfBirth: person.dateOfBirth ?? "",
  gender: person.gender ?? "",
  mobileNumber: person.mobileNumber ?? "",
  email: person.email ?? "",
});

const mapContextYouthProfile = (
  profile?: YouthProfile | null,
): YouthProfile => ({
  ...emptyYouthProfile(),
  ...(profile ?? {}),
});

const mergeContextConsents = (
  defaultConsents: Consent[],
  loadedConsents?: Consent[],
) => {
  const loadedByCode = new Map(
    (loadedConsents ?? []).map((consent) => [consent.consentTypeCode, consent]),
  );
  const loadedByName = new Map(
    (loadedConsents ?? []).map((consent) => [consent.consentTypeName, consent]),
  );

  return defaultConsents.map((defaultConsent) => {
    const loaded =
      loadedByCode.get(defaultConsent.consentTypeCode) ??
      loadedByName.get(defaultConsent.consentTypeName);
    return {
      ...defaultConsent,
      ...loaded,
      consentTypeCode: defaultConsent.consentTypeCode,
    };
  });
};

const mergeContextAgreements = (
  defaultAgreements: Agreement[],
  loadedAgreements?: Agreement[],
) => {
  const loadedByCode = new Map(
    (loadedAgreements ?? []).map((agreement) => [
      agreement.agreementCode,
      agreement,
    ]),
  );
  const loadedByTitle = new Map(
    (loadedAgreements ?? []).map((agreement) => [agreement.title, agreement]),
  );

  return defaultAgreements.map((defaultAgreement) => {
    const loaded =
      loadedByCode.get(defaultAgreement.agreementCode) ??
      loadedByTitle.get(defaultAgreement.title);
    return {
      ...defaultAgreement,
      ...loaded,
      agreementCode: defaultAgreement.agreementCode,
    };
  });
};

const shouldShowChildProfileSections = (person: { dateOfBirth: string }) => {
  const age = calculateAge(person.dateOfBirth);
  return age === null || age < 18;
};

const hasYouthProfileData = (profile?: YouthProfile | null) =>
  Boolean(
    profile &&
      (profile.schoolName.trim() ||
        profile.schoolYear.trim() ||
        profile.isLookedAfterChild !== null ||
        profile.knownToSocialServices !== null ||
        profile.hasEHCP !== null ||
        profile.underCAMHS !== null ||
        profile.medicalInformation.trim() ||
        profile.additionalNeeds.trim() ||
        profile.dietaryRequirements.trim() ||
        profile.gpName.trim() ||
        profile.gpContactNumber.trim()),
  );

const hasPersonDetails = (person: FamilyPerson) =>
  Boolean(person.firstName.trim()) ||
  Boolean(person.lastName.trim()) ||
  Boolean(person.dateOfBirth.trim()) ||
  Boolean(person.gender.trim()) ||
  Boolean(person.mobileNumber?.trim()) ||
  Boolean(person.email?.trim());

export default function PartyFamily({
  partyId,
  initialStep = 0,
  onCompleted,
  saveLabel = "Save",
  initialContext,
  consentTemplates,
  agreementTemplates,
}: PartyFamilyProps) {
  const partyContext = usePartyFormContext();
  const reviewFirst = partyContext.mode === PartyFormMode.Admin;
  const [isReviewing, setIsReviewing] = useState(reviewFirst && Boolean(partyId || initialContext));
  const location = useLocation();
  const { data } = useStaticData();
  const { selectedTenant } = useAuth();
  const normalizedInitialStep = Math.min(
    Math.max(initialStep, 0),
    steps.length - 1,
  );
  const [activeStep, setActiveStep] = useState(normalizedInitialStep);
  const [isAddingMember, setIsAddingMember] = useState(false);
  const childEntryRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (isAddingMember) {
      childEntryRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      childEntryRef.current?.querySelector<HTMLInputElement>('input[name="firstName"]')?.focus({ preventScroll: true });
    }
  }, [isAddingMember]);
  const [newMember, setNewMember] = useState<FamilyPerson>(
    createEmptyPerson,
  );
  const [newMemberYouthProfile, setNewMemberYouthProfile] =
    useState<YouthProfile>(emptyYouthProfile);
  const createMemberConsents = useCallback(
    () =>
      (consentTemplates !== undefined
        ? consentTemplates
        : createDefaultConsents()
      ).map((consent) => ({ ...consent })),
    [consentTemplates],
  );
  const createMemberAgreements = useCallback(
    () =>
      (agreementTemplates !== undefined
        ? agreementTemplates
        : createDefaultAgreements()
      ).map((agreement) => ({ ...agreement })),
    [agreementTemplates],
  );
  const [newMemberConsents, setNewMemberConsents] = useState<Consent[]>(
    createMemberConsents,
  );
  const [newMemberAgreements, setNewMemberAgreements] = useState<Agreement[]>(
    createMemberAgreements,
  );
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [loadingFamilyContext, setLoadingFamilyContext] = useState(false);
  const [addressList, setAddressList] = useState<PartyAddress[]>([]);
  const [editingHouseholdId, setEditingHouseholdId] = useState<number | null>(
    null,
  );
  const tenantId = selectedTenant?.tenantId;
  const registrationServiceId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const value = Number(params.get("serviceId"));
    return Number.isFinite(value) && value > 0
      ? value
      : partyContext.serviceId ?? null;
  }, [location.search, partyContext.serviceId]);
  const registrationMemberType = useMemo<RegistrationMemberType>(() => {
    const params = new URLSearchParams(location.search);
    const value = params.get("memberType") ?? "ServiceUser";
    return validMemberTypes.includes(value as RegistrationMemberType)
      ? (value as RegistrationMemberType)
      : "ServiceUser";
  }, [location.search]);
  const [registrationRequirements, setRegistrationRequirements] = useState<
    ServiceRegistrationRequirementDto[]
  >([]);
  const [loadingRegistrationRequirements, setLoadingRegistrationRequirements] =
    useState(false);
  const [selectedServiceIds, setSelectedServiceIds] = useState<number[]>(
    registrationServiceId ? [registrationServiceId] : [],
  );

  const [familyRegistration, setFamilyRegistration] =
    useState<FamilyRegistration>({
      primaryContact: createEmptyPerson(),
      secondaryContact: createEmptyPerson(),
      secondaryContactStaysAtSameAddress: true,
      household: {},
      householdMembers: [],
      relationships: [],
      partyRelationships: [],
      youthProfiles: {},
      consents: {},
      agreements: {},
    });

  useEffect(() => {
    if (!registrationServiceId) {
      setRegistrationRequirements([]);
      return;
    }

    if (partyContext.registrationRequirements) {
      setRegistrationRequirements(partyContext.registrationRequirements);
      return;
    }

    const loadRequirements = async () => {
      try {
        setLoadingRegistrationRequirements(true);
        const result = await api<ServiceRegistrationRequirementDto[]>(
          `/api/services/${registrationServiceId}/registration-requirements?memberType=${registrationMemberType}`,
          {
            suppressLoading: true,
            notifyError: false,
          },
        );
        setRegistrationRequirements(result);
      } catch (error) {
        console.error("Failed to load registration requirements", error);
        setRegistrationRequirements([]);
      } finally {
        setLoadingRegistrationRequirements(false);
      }
    };

    loadRequirements();
  }, [
    partyContext.registrationRequirements,
    registrationMemberType,
    registrationServiceId,
  ]);

  useEffect(() => {
    if (!initialContext) return;

    const primaryContact = mapContextPerson(
      initialContext.primaryContact,
      "primary-contact",
    );
    const secondaryContact = initialContext.secondaryContact
      ? mapContextPerson(initialContext.secondaryContact, "secondary-contact")
      : createEmptyPerson();
    const householdMembers = initialContext.members.map((member, index) =>
      mapContextPerson(member, `profile-member-${index}`),
    );

    setFamilyRegistration({
      primaryContact,
      secondaryContact,
      secondaryContactStaysAtSameAddress:
        initialContext.secondaryContactStaysAtSameAddress ?? true,
      household: initialContext.household ?? {},
      householdMembers,
      relationships: initialContext.relationships ?? [],
      partyRelationships: [],
      youthProfiles: Object.fromEntries(
        householdMembers.map((member, index) => [
          member.id,
          mapContextYouthProfile(initialContext.members[index]?.youthProfile),
        ]),
      ),
      consents: Object.fromEntries(
        householdMembers.map((member, index) => [
          member.id,
          mergeContextConsents(
            createMemberConsents(),
            initialContext.members[index]?.consents,
          ),
        ]),
      ),
      agreements: Object.fromEntries(
        householdMembers.map((member, index) => [
          member.id,
          mergeContextAgreements(
            createMemberAgreements(),
            initialContext.members[index]?.agreements,
          ),
        ]),
      ),
    });
    setAddressList(
      initialContext.household.partyAddress
        ? [initialContext.household.partyAddress]
        : [],
    );
    setSelectedMemberId(null);
    setIsReviewing(reviewFirst);
    setActiveStep(reviewFirst ? steps.length - 1 : normalizedInitialStep);
  }, [createMemberAgreements, createMemberConsents, initialContext, normalizedInitialStep, reviewFirst]);

  useEffect(() => {
    setIsReviewing(reviewFirst && Boolean(partyId || initialContext));
    if (!partyId) return;

    const loadFamilyContext = async () => {
      try {
        setLoadingFamilyContext(true);

        const context = await api<FamilyContext>(
          `/api/party/family-context/${partyId}`,
          {
            method: "GET",
          },
        );

        const primaryContact = mapContextPerson(context.primaryContact);
        const secondaryContact = context.secondaryContact
          ? mapContextPerson(context.secondaryContact, "secondary-contact")
          : createEmptyPerson();
        const householdMembers = context.members.map((member) =>
          mapContextPerson(member),
        );
        const selectedMember = householdMembers.find(
          (member) => member.partyId === context.selectedPartyId,
        );
        const selectedContextMemberId = selectedMember?.id ?? null;
        const householdPartyAddress = context.household.partyAddress;

        setFamilyRegistration({
          primaryContact,
          secondaryContact,
          secondaryContactStaysAtSameAddress:
            context.secondaryContactStaysAtSameAddress ?? true,
          household: context.household ?? {},
          householdMembers,
          relationships: context.relationships ?? [],
          partyRelationships: context.partyRelationships ?? [],
          youthProfiles: Object.fromEntries(
            context.members.map((member) => [
              String(member.partyId),
              mapContextYouthProfile(member.youthProfile),
            ]),
          ),
          consents: Object.fromEntries(
            context.members.map((member) => [
              String(member.partyId),
              mergeContextConsents(createMemberConsents(), member.consents),
            ]),
          ),
          agreements: Object.fromEntries(
            context.members.map((member) => [
              String(member.partyId),
              mergeContextAgreements(createMemberAgreements(), member.agreements),
            ]),
          ),
        });

        setEditingHouseholdId(context.householdId ?? null);
        setAddressList(householdPartyAddress ? [householdPartyAddress] : []);
        setIsAddingMember(false);
        setNewMember(createEmptyPerson());
        setSelectedMemberId(selectedContextMemberId);
        setActiveStep(reviewFirst ? steps.length - 1 : selectedContextMemberId ? 2 : normalizedInitialStep);
      } catch (error) {
        console.error("Failed to load family context", error);
        notify.error("Failed to load family context.");
      } finally {
        setLoadingFamilyContext(false);
      }
    };

    loadFamilyContext();
  }, [createMemberAgreements, createMemberConsents, normalizedInitialStep, partyId, reviewFirst, initialContext]);

  const relationshipTypes = useMemo<RelationshipType[]>(() => {
    const grouped = data?.relationshipTypes ?? {};
    return (
      grouped[RELATIONSHIP_CATEGORIES.HOUSEHOLD] ??
      Object.values(grouped).flat()
    ) as RelationshipType[];
  }, [data]);

  const childRelationshipTypeId = useMemo(
    () =>
      relationshipTypes.find((type) => {
        const code = type.code?.trim().toUpperCase();
        const name = type.name?.trim().toLowerCase();
        return code === "CHILD" || name === "child";
      })?.relationshipTypeId,
    [relationshipTypes],
  );

  const createEmptyChild = useCallback(
    (): FamilyPerson => ({
      ...createEmptyPerson(),
      relationshipTypeId: childRelationshipTypeId,
    }),
    [childRelationshipTypeId],
  );

  useEffect(() => {
    if (!isAddingMember || !childRelationshipTypeId) return;

    setNewMember((current) =>
      current.relationshipTypeId
        ? current
        : { ...current, relationshipTypeId: childRelationshipTypeId },
    );
  }, [childRelationshipTypeId, isAddingMember]);

  const getRequirementLevel = useCallback(
    (componentKey: string) =>
      registrationRequirements.find(
        (requirement) =>
          requirement.componentKey.toLowerCase() === componentKey.toLowerCase(),
      )?.requirementLevel ??
      (componentKey === "EmergencyContact"
        ? "Hidden"
        : componentKey === "ContactDetails" ||
      componentKey === "PrimaryContact" ||
      componentKey === "Address"
        ? "Required"
        : "Optional"),
    [registrationRequirements],
  );

  const isComponentRequired = useCallback(
    (componentKey: string) => getRequirementLevel(componentKey) === "Required",
    [getRequirementLevel],
  );

  const isComponentHidden = useCallback(
    (componentKey: string) => getRequirementLevel(componentKey) === "Hidden",
    [getRequirementLevel],
  );

  const editableHouseholdMembers = useMemo<HouseholdMember[]>(
    () =>
      familyRegistration.householdMembers
        .map((member) => ({
          id: member.id,
          firstName: member.firstName,
          lastName: member.lastName,
          dateOfBirth: member.dateOfBirth,
          gender: member.gender,
          relationshipTypeId: member.relationshipTypeId,
        })),
    [familyRegistration.householdMembers],
  );

  const selectedMember = useMemo(
    () =>
      familyRegistration.householdMembers.find(
        (member) => member.id === selectedMemberId,
      ) ?? null,
    [familyRegistration.householdMembers, selectedMemberId],
  );

  const selectedHouseholdPerson = useMemo(() => {
    return selectedMember
      ? {
          person: selectedMember,
          role: "Child" as const,
        }
      : null;
  }, [selectedMember]);

  const selectedHouseholdPersonIsChild =
    selectedHouseholdPerson?.role === "Child" &&
    shouldShowChildProfileSections(selectedHouseholdPerson.person);


  const newMemberDraft: HouseholdMember = useMemo(
    () => ({
      id: newMember.id || "new-household-member",
      firstName: newMember.firstName,
      lastName: newMember.lastName,
      dateOfBirth: newMember.dateOfBirth,
      gender: newMember.gender,
      relationshipTypeId: newMember.relationshipTypeId,
    }),
    [newMember],
  );

  const updatePrimaryContact = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFamilyRegistration((prev) => ({
      ...prev,
      primaryContact: {
        ...prev.primaryContact,
        [name]: value,
      },
    }));
  };

  const updatePrimaryContactDateOfBirth = (value: string | null) => {
    setFamilyRegistration((prev) => ({
      ...prev,
      primaryContact: {
        ...prev.primaryContact,
        dateOfBirth: value ?? "",
      },
    }));
  };

  const updateSecondaryContact = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFamilyRegistration((prev) => ({
      ...prev,
      secondaryContact: {
        ...prev.secondaryContact,
        [name]: name === "relationshipTypeId" ? Number(value) || undefined : value,
      },
    }));
  };

  const updateSecondaryContactDateOfBirth = (value: string | null) => {
    setFamilyRegistration((prev) => ({
      ...prev,
      secondaryContact: {
        ...prev.secondaryContact,
        dateOfBirth: value ?? "",
      },
    }));
  };

  const updateSecondaryContactSameAddress = (checked: boolean) => {
    setFamilyRegistration((prev) => ({
      ...prev,
      secondaryContactStaysAtSameAddress: checked,
    }));
  };

  const updateNewMember = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setNewMember((prev) => ({
      ...prev,
      [name]: name === "relationshipTypeId" ? Number(value) || undefined : value,
    }));
  };

  const updateNewMemberDateOfBirth = (value: string | null) => {
    setNewMember((prev) => ({
      ...prev,
      dateOfBirth: value ?? "",
    }));
  };

  const updateSelectedMember = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    if (!selectedMemberId) return;

    const { name, value } = e.target;

    setFamilyRegistration((prev) => ({
      ...prev,
      householdMembers: prev.householdMembers.map((member) =>
        member.id === selectedMemberId
          ? {
              ...member,
              [name]:
                name === "relationshipTypeId"
                  ? Number(value) || undefined
                  : value,
            }
          : member,
      ),
    }));
  };

  const updateSelectedMemberDateOfBirth = (value: string | null) => {
    if (!selectedMemberId) return;

    setFamilyRegistration((prev) => ({
      ...prev,
      householdMembers: prev.householdMembers.map((member) =>
        member.id === selectedMemberId
          ? {
              ...member,
              dateOfBirth: value ?? "",
            }
          : member,
      ),
    }));
  };

  const handleAddressChange = (addresses: PartyAddress[]) => {
    setAddressList(addresses);
    const householdPartyAddress = addresses[0];

    setFamilyRegistration((prev) => ({
      ...prev,
      household: {
        ...prev.household,
        household: householdPartyAddress?.address,
        partyAddress: householdPartyAddress,
      },
    }));
  };

  const startAddingChild = () => {
    setSelectedMemberId(null);
    setNewMember(createEmptyChild());
    setNewMemberYouthProfile(emptyYouthProfile());
    setNewMemberConsents(createMemberConsents());
    setNewMemberAgreements(createMemberAgreements());
    setIsAddingMember(true);
  };

  const addHouseholdMember = () => {
    if (!newMember.firstName.trim() || !newMember.lastName.trim()) return;
    if (!newMember.dateOfBirth.trim()) {
      notify.error("Enter the child's date of birth before adding them.");
      return;
    }
    if (
      isComponentRequired("YouthProfile") &&
      shouldShowChildProfileSections(newMember) &&
      !hasYouthProfileData(newMemberYouthProfile) &&
      newMemberConsents.length === 0 &&
      newMemberAgreements.length === 0
    ) {
      notify.error("Enter at least one youth profile detail for this child.");
      return;
    }
    if (
      isComponentRequired("EmergencyContact") &&
      !hasCompleteEmergencyContact(newMember.emergencyContact)
    ) {
      notify.error(
        "Enter the child's emergency contact name, relationship and a phone number or email address.",
      );
      return;
    }

    const memberIndex = familyRegistration.householdMembers.length;
    const memberToAdd = {
      ...newMember,
      id: newMember.id || createMemberId(),
    };

    setFamilyRegistration((prev) => ({
      ...prev,
      householdMembers: [...prev.householdMembers, memberToAdd],
      relationships: memberToAdd.relationshipTypeId
        ? [
            ...prev.relationships,
            {
              memberIndex,
              relationshipTypeId: memberToAdd.relationshipTypeId,
            },
          ]
        : prev.relationships,
      youthProfiles: {
        ...prev.youthProfiles,
        [memberToAdd.id]: newMemberYouthProfile,
      },
      consents: {
        ...prev.consents,
        [memberToAdd.id]: newMemberConsents,
      },
      agreements: {
        ...prev.agreements,
        [memberToAdd.id]: newMemberAgreements,
      },
    }));

    setNewMember(createEmptyChild());
    setNewMemberYouthProfile(emptyYouthProfile());
    setNewMemberConsents(createMemberConsents());
    setNewMemberAgreements(createMemberAgreements());
    setSelectedMemberId(memberToAdd.id);
    setIsAddingMember(false);
  };

  const updateYouthProfile = (childId: string, value: YouthProfile) => {
    setFamilyRegistration((prev) => ({
      ...prev,
      youthProfiles: {
        ...prev.youthProfiles,
        [childId]: value,
      },
    }));
  };

  const updateConsents = (childId: string, value: Consent[]) => {
    setFamilyRegistration((prev) => ({
      ...prev,
      consents: {
        ...prev.consents,
        [childId]: value,
      },
    }));
  };

  const updateAgreements = (childId: string, value: Agreement[]) => {
    setFamilyRegistration((prev) => ({
      ...prev,
      agreements: {
        ...prev.agreements,
        [childId]: value,
      },
    }));
  };

  const updateSelectedMemberEmergencyContact = (value: EmergencyContact) => {
    if (!selectedMemberId) return;

    setFamilyRegistration((prev) => ({
      ...prev,
      householdMembers: prev.householdMembers.map((member) =>
        member.id === selectedMemberId
          ? { ...member, emergencyContact: value }
          : member,
      ),
    }));
  };

  const buildFamilyPayload = () => ({
    serviceIds: selectedServiceIds,
    memberType: registrationMemberType,
    primaryContact: {
      ...familyRegistration.primaryContact,
      dateOfBirth: familyRegistration.primaryContact.dateOfBirth.trim() || null,
    },
    secondaryContact: hasPersonDetails(familyRegistration.secondaryContact)
      ? {
          ...familyRegistration.secondaryContact,
          dateOfBirth:
            familyRegistration.secondaryContact.dateOfBirth.trim() || null,
        }
      : null,
    secondaryContactStaysAtSameAddress:
      familyRegistration.secondaryContactStaysAtSameAddress,
    household: familyRegistration.household,
    relationships: familyRegistration.relationships,
    members: familyRegistration.householdMembers.map((member) => ({
      ...member,
      dateOfBirth: member.dateOfBirth.trim() || null,
      youthProfile: shouldShowChildProfileSections(member)
        ? {
            ...(familyRegistration.youthProfiles[member.id] ?? emptyYouthProfile()),
            consents:
              familyRegistration.consents[member.id] ?? createMemberConsents(),
            agreements:
              familyRegistration.agreements[member.id] ?? createMemberAgreements(),
          }
        : null,
      // Keep these during the transition so older API deployments can still read them.
      consents: shouldShowChildProfileSections(member)
        ? familyRegistration.consents[member.id] ?? createMemberConsents()
        : [],
      agreements: shouldShowChildProfileSections(member)
        ? familyRegistration.agreements[member.id] ?? createMemberAgreements()
        : [],
    })),
  });

  const updateFoundPerson = async () => {
    const primaryContact = familyRegistration.primaryContact;

    if (!primaryContact.partyId) {
      throw new Error("Party ID is required to update this person.");
    }

    const existingPerson = await api<PersonDetails>("/api/person/get-by-id", {
      method: "POST",
      body: primaryContact.partyId,
    });

    await api<PersonDetails>("/api/person/update", {
      method: "POST",
      body: {
        ...existingPerson,
        firstName: primaryContact.firstName,
        lastName: primaryContact.lastName,
        dateOfBirth:
          primaryContact.dateOfBirth?.trim() !== ""
            ? primaryContact.dateOfBirth
            : null,
        gender: primaryContact.gender,
        contactMethods: existingPerson.contactMethods ?? [],
        contactPreferences: existingPerson.contactPreferences ?? [],
        householdMemberships: existingPerson.householdMemberships ?? [],
        partyAddresses: existingPerson.partyAddresses ?? [],
      },
    });
  };

  const saveFamily = async () => {
    setSaving(true);

    try {
      if (
        !editingHouseholdId &&
        familyRegistration.primaryContact.partyId &&
        familyRegistration.householdMembers.length === 0
      ) {
        await updateFoundPerson();
        notify.success("Person updated successfully.");
        return;
      }

      const payload = buildFamilyPayload();

      if (partyContext.mode === PartyFormMode.PublicRegistration) {
        await api<void>("/api/public/register", {
          method: "POST",
          auth: "none",
          body: {
            partyType: "Family",
            serviceId: registrationServiceId,
            family: payload,
            agreementResponses: partyContext.agreementResponses ?? [],
          },
        });
        notify.success("Family registration submitted for review.");
        onCompleted?.();
        return;
      }

      if (partyContext.mode === PartyFormMode.ProfileCompletion) {
        await api<void>(
          `/api/public/profile/${encodeURIComponent(partyContext.invitationToken ?? "")}`,
          {
            method: "PUT",
            auth: "none",
            body: {
              family: payload,
              agreementResponses: partyContext.agreementResponses ?? [],
            },
          },
        );
        notify.success("Family profile completed.");
        onCompleted?.();
        return;
      }

      const serviceLedRegistration =
        !editingHouseholdId && registrationServiceId && selectedServiceIds.includes(registrationServiceId);

      const result = await api<FamilyRegistrationResult>(
        editingHouseholdId
          ? `/api/party/family/${editingHouseholdId}`
          : serviceLedRegistration
            ? `/api/services/${registrationServiceId}/members/register`
            : "/api/party/family",
        {
          method: editingHouseholdId ? "PUT" : "POST",
          body: payload,
        },
      );

      setEditingHouseholdId(result.householdId);
      if (reviewFirst) {
        setIsReviewing(true);
        setActiveStep(steps.length - 1);
      }

      notify.success(
        editingHouseholdId
          ? "Family details updated."
          : "Family registration created.",
      );
    } finally {
      setSaving(false);
    }
  };


  const renderPersonFields = (
    person: FamilyPerson,
    onChange: (
      e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    ) => void,
    onDateOfBirthChange: (value: string | null) => void,
    dateOfBirthRequired = false,
  ) => (
    <>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">First Name</label>
          <input
            name="firstName"
            className="form-input"
            value={person.firstName}
            onChange={onChange}
          />
        </div>
        <div className="form-group">
          <label className="form-label">Last Name</label>
          <input
            name="lastName"
            className="form-input"
            value={person.lastName}
            onChange={onChange}
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label className="form-label">
            Date of Birth {dateOfBirthRequired ? "*" : ""}
          </label>
          <AppDatePicker
            value={person.dateOfBirth}
            maxDate={dayjs()}
            onChange={onDateOfBirthChange}
          />
        </div>
        <div className="form-group">
          <label className="form-label">Gender</label>
          <select
            name="gender"
            className="form-input"
            value={person.gender}
            onChange={onChange}
          >
            <option value="">Select</option>
            <option value="Female">Female</option>
            <option value="Male">Male</option>
            <option value="Other">Other</option>
            <option value="Prefer not to say">Prefer not to say</option>
          </select>
        </div>
      </div>

    </>
  );

  const renderContactFields = (
    person: FamilyPerson,
    onChange: (
      e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    ) => void,
  ) => (
    <div className="form-row">
      <div className="form-group">
        <label className="form-label">
          Mobile Number {isComponentRequired("ContactDetails") ? "*" : ""}
        </label>
        <input
          name="mobileNumber"
          className="form-input"
          value={person.mobileNumber ?? ""}
          onChange={onChange}
        />
      </div>
      <div className="form-group">
        <label className="form-label">
          Email {isComponentRequired("ContactDetails") ? "*" : ""}
        </label>
        <input
          type="email"
          name="email"
          className="form-input"
          value={person.email ?? ""}
          onChange={onChange}
        />
      </div>
    </div>
  );

  const renderContactDetailsCard = (
    title: string,
    hint: string,
    person: FamilyPerson,
    onChange: (
      e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    ) => void,
  ) => {
    if (isComponentHidden("ContactDetails")) return null;

    return (
      <div className="card">
        <div className="card-header">
          <div className="card-title">{title}</div>
          <div className="field-hint">{hint}</div>
        </div>
        {renderContactFields(person, onChange)}
      </div>
    );
  };

  const renderStep = () => {
    if (!isReviewing && activeStep === 0) {
      return (
        <div className="family-contact-edit-sections">
          <div className="card">
            <div className="card-header">
              <div className="card-title">Primary Contact</div>
              <div className="field-hint">
                Capture the main adult contact for this family.
              </div>
            </div>
            {renderPersonFields(
              familyRegistration.primaryContact,
              updatePrimaryContact,
              updatePrimaryContactDateOfBirth,
            )}
          </div>

          {renderContactDetailsCard(
            "Contact Details",
            "Capture phone and email details for the primary contact.",
            familyRegistration.primaryContact,
            updatePrimaryContact,
          )}

          {!isComponentHidden("Address") && (
            <AddressCard
              partyId={0}
              addresses={addressList}
              onChange={handleAddressChange}
              allowAddAnother={partyContext.mode === PartyFormMode.Admin}
            />
          )}
        </div>
      );
    }

    if (!isReviewing && activeStep === 1) {
      return (
        <div className="family-contact-edit-sections">
          <div className="card">
            <div className="card-header">
              <div className="card-title">Secondary Contact</div>
            </div>
            <div className="field-hint">
              Add another adult contact for the family if needed.
            </div>
            {renderPersonFields(
              familyRegistration.secondaryContact,
              updateSecondaryContact,
              updateSecondaryContactDateOfBirth,
            )}
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Relationship to primary contact</label>
                <select
                  name="relationshipTypeId"
                  className="form-input"
                  value={familyRegistration.secondaryContact.relationshipTypeId ?? ""}
                  onChange={updateSecondaryContact}
                >
                  <option value="">Select</option>
                  {relationshipTypes.map((type) => (
                    <option
                      key={type.relationshipTypeId}
                      value={type.relationshipTypeId}
                    >
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {!isComponentHidden("Address") && (
              <div className="form-row">
                <label className="option-item">
                  <input
                    type="checkbox"
                    checked={familyRegistration.secondaryContactStaysAtSameAddress}
                    onChange={(event) =>
                      updateSecondaryContactSameAddress(event.target.checked)
                    }
                  />
                  Stays at the same address as the primary contact
                </label>
              </div>
            )}
          </div>

          {renderContactDetailsCard(
            "Secondary Contact Details",
            "Capture phone and email details for the secondary contact.",
            familyRegistration.secondaryContact,
            updateSecondaryContact,
          )}
        </div>
      );
    }

    if (!isReviewing && activeStep === 2) {
      return (
        <div className="family-child-entry">
          <div className="card">
            <div className="card-header party-card-header">
              <div className="card-header-row party-card-header-row">
                <div className="card-title">Children</div>
              </div>
            </div>

            {familyRegistration.householdMembers.length === 0 ? (
              <p className="family-children-empty">No children added yet. Use Add Child to add a child to this family.</p>
            ) : (
              <div className="family-children-list" aria-label="Children">
                {familyRegistration.householdMembers.map((member) => (
                  <button
                    key={member.id}
                    type="button"
                    className="family-child-choice"
                    aria-pressed={!isAddingMember && selectedMemberId === member.id}
                    onClick={() => {
                      setIsAddingMember(false);
                      setSelectedMemberId(member.id);
                    }}
                  >
                    {getDisplayName(member)}
                  </button>
                ))}
              </div>
            )}

          </div>

          {isAddingMember && (
            <div className="family-child-entry" ref={childEntryRef}>
              <div className="card">
                <div className="card-header">
                  <div className="card-title">Child Details</div>
                </div>

                {renderPersonFields(
                  newMember,
                  updateNewMember,
                  updateNewMemberDateOfBirth,
                  true,
                )}
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Relationship</label>
                    <select
                      name="relationshipTypeId"
                      className="form-input"
                      value={newMember.relationshipTypeId ?? ""}
                      onChange={updateNewMember}
                    >
                      <option value="">Select</option>
                      {relationshipTypes.map((type) => (
                        <option
                          key={type.relationshipTypeId}
                          value={type.relationshipTypeId}
                        >
                          {type.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {!isComponentHidden("EmergencyContact") && (
                <EmergencyContactCard
                  value={newMember.emergencyContact}
                  required={isComponentRequired("EmergencyContact")}
                  onChange={(emergencyContact) =>
                    setNewMember((prev) => ({ ...prev, emergencyContact }))
                  }
                />
              )}

              {shouldShowChildProfileSections(newMember) &&
                !isComponentHidden("YouthProfile") && (
                <div className="child-registration-sections">
                  <section className="child-registration-section child-registration-section--profile">
                    <YouthProfileCard
                      compact
                      child={newMemberDraft}
                      value={newMemberYouthProfile}
                      onChange={setNewMemberYouthProfile}
                    />
                  </section>

                  {!isComponentHidden("Consents") &&
                    newMemberConsents.length > 0 && (
                  <section className="child-registration-section child-registration-section--consents">
                    <ConsentCard
                      compact
                      child={newMemberDraft}
                      consents={newMemberConsents}
                      onChange={setNewMemberConsents}
                    />
                  </section>
                  )}

                  {!isComponentHidden("Agreements") &&
                    newMemberAgreements.length > 0 && (
                  <section className="child-registration-section child-registration-section--agreements">
                    <AgreementCard
                      compact
                      agreements={newMemberAgreements}
                      onChange={setNewMemberAgreements}
                    />
                  </section>
                  )}
                </div>
              )}

            </div>
          )}

          {selectedHouseholdPerson && (
            <div className="family-child-entry">
              <div className="card">
                <div className="card-header">
                  <div className="card-title">
                    Child Details
                  </div>
                </div>

                {renderPersonFields(
                  selectedHouseholdPerson.person,
                  updateSelectedMember,
                  updateSelectedMemberDateOfBirth,
                )}

                {selectedHouseholdPerson.role === "Child" && (
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Relationship</label>
                      <select
                        name="relationshipTypeId"
                        className="form-input"
                        value={selectedHouseholdPerson.person.relationshipTypeId ?? ""}
                        onChange={updateSelectedMember}
                      >
                        <option value="">Select</option>
                        {relationshipTypes.map((type) => (
                          <option
                            key={type.relationshipTypeId}
                            value={type.relationshipTypeId}
                          >
                            {type.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {!isComponentHidden("EmergencyContact") && (
                <EmergencyContactCard
                  value={selectedHouseholdPerson.person.emergencyContact}
                  required={isComponentRequired("EmergencyContact")}
                  onChange={updateSelectedMemberEmergencyContact}
                />
              )}

              {partyContext.mode === PartyFormMode.Admin && (
                <ServiceEnrollmentCard
                  key={selectedHouseholdPerson.person.partyId ?? selectedHouseholdPerson.person.id}
                  partyId={selectedHouseholdPerson.person.partyId ?? null}
                  tenantId={tenantId}
                  selectedServices={selectedServiceIds}
                  onSelectedServicesChange={setSelectedServiceIds}
                  registrationServiceId={registrationServiceId}
                  registrationServiceName={partyContext.serviceName}
                  registrationMemberType={
                    selectedHouseholdPerson.role === "Child"
                      ? "ServiceUser"
                      : "PrimaryContact"
                  }
                />
              )}

              {selectedHouseholdPersonIsChild && !isComponentHidden("YouthProfile") && (
                <div className="child-registration-sections">
                  <section className="child-registration-section child-registration-section--profile">
                    <YouthProfileCard
                      compact
                      child={selectedHouseholdPerson.person}
                      value={
                        familyRegistration.youthProfiles[
                          selectedHouseholdPerson.person.id
                        ] ?? emptyYouthProfile()
                      }
                      onChange={(value) =>
                        updateYouthProfile(selectedHouseholdPerson.person.id, value)
                      }
                    />
                  </section>

                  {!isComponentHidden("Consents") &&
                    createMemberConsents().length > 0 && (
                  <section className="child-registration-section child-registration-section--consents">
                    <ConsentCard
                      compact
                      child={selectedHouseholdPerson.person}
                      consents={
                        familyRegistration.consents[
                          selectedHouseholdPerson.person.id
                        ] ?? createMemberConsents()
                      }
                      onChange={(value) =>
                        updateConsents(selectedHouseholdPerson.person.id, value)
                      }
                    />
                  </section>
                  )}

                  {!isComponentHidden("Agreements") &&
                    createMemberAgreements().length > 0 && (
                  <section className="child-registration-section child-registration-section--agreements">
                    <AgreementCard
                      compact
                      agreements={
                        familyRegistration.agreements[
                          selectedHouseholdPerson.person.id
                        ] ?? createMemberAgreements()
                      }
                      onChange={(value) =>
                        updateAgreements(selectedHouseholdPerson.person.id, value)
                      }
                    />
                  </section>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      );
    }

    const householdAddress = familyRegistration.household.partyAddress?.address ?? familyRegistration.household.household;
    const addressLine =
      [
        householdAddress?.addressLine1,
        householdAddress?.town,
        householdAddress?.county,
        householdAddress?.postCode,
      ]
        .filter(Boolean)
        .join(", ") || "-";

    const responseClass = (value: boolean | null | undefined) =>
      value === true
        ? "family-review-pill family-review-pill--yes"
        : value === false
          ? "family-review-pill family-review-pill--no"
          : "family-review-pill family-review-pill--empty";

    const responseLabel = (value: boolean | null | undefined) =>
      value === true ? "Yes" : value === false ? "No" : "Not answered";

    return (
      <div className="card family-review">
        {!isReviewing && <div className="card-header"><div className="card-title">Review Family</div></div>}

        <div className="family-review-overview">
          <section className="family-review-panel family-contact-summary">
            <div className="family-review-panel-title">Primary Contact</div>
            <dl className="family-review-details family-contact-row">
              <div>
                <dt>Name</dt>
                <dd>{getDisplayName(familyRegistration.primaryContact)}</dd>
              </div>
              {!isComponentHidden("Address") && (
                <div className="family-contact-address">
                  <dt>Address</dt>
                  <dd>{addressLine}</dd>
                </div>
              )}
              {!isComponentHidden("ContactDetails") && (
                <>
                  <div>
                    <dt>Mobile</dt>
                    <dd>{familyRegistration.primaryContact.mobileNumber || "-"}</dd>
                  </div>
                  <div>
                    <dt>Email</dt>
                    <dd>{familyRegistration.primaryContact.email || "-"}</dd>
                  </div>
                </>
              )}
            </dl>
          </section>

          {hasSecondaryContact && (
            <section className="family-review-panel family-contact-summary">
              <div className="family-review-panel-title">Secondary Contact</div>
              <dl className="family-review-details family-contact-row">
                <div>
                  <dt>Name</dt>
                  <dd>{getDisplayName(familyRegistration.secondaryContact)}</dd>
                </div>
                {!isComponentHidden("Address") && (
                  <div className="family-contact-address">
                    <dt>Address</dt>
                    <dd>{familyRegistration.secondaryContactStaysAtSameAddress ? addressLine : "-"}</dd>
                  </div>
                )}
                {!isComponentHidden("ContactDetails") && (
                  <>
                    <div>
                      <dt>Mobile</dt>
                      <dd>{familyRegistration.secondaryContact.mobileNumber || "-"}</dd>
                    </div>
                    <div>
                      <dt>Email</dt>
                      <dd>{familyRegistration.secondaryContact.email || "-"}</dd>
                    </div>
                  </>
                )}
              </dl>
            </section>
          )}

        </div>

        <section className="family-review-panel">
          <div className="family-review-section-header">
            <div className="family-review-panel-title">Children</div>
          </div>
          {editableHouseholdMembers.length === 0 && <p>No children added.</p>}
            <div className="family-review-child-grid">
              {editableHouseholdMembers.map((child) => {
                const profile = familyRegistration.youthProfiles[child.id];
                const consents =
                  familyRegistration.consents[child.id] ?? createMemberConsents();
                const agreements =
                  familyRegistration.agreements[child.id] ??
                  createMemberAgreements();

                return (
                  <article className="family-review-child" key={child.id}>
                    <div className="family-review-child-summary">
                    <div className="family-review-child-header">
                      <h3 className="family-review-child-name" title={getDisplayName(child)}>
                        {getDisplayName(child)}
                      </h3>
                      <span className="family-review-age">
                        Age {calculateAgeLabel(child.dateOfBirth)}
                      </span>
                    </div>

                    <dl className="family-review-details family-review-details--compact">
                      <div>
                        <dt>School</dt>
                        <dd>{profile?.schoolName || "-"}</dd>
                      </div>
                      <div>
                        <dt>Year</dt>
                        <dd>{profile?.schoolYear || "-"}</dd>
                      </div>
                    </dl>
                    </div>

                    <div className="family-review-subsection">
                      <div className="family-review-subtitle">Consents</div>
                      <div className="family-review-pill-row">
                        {consents.map((consent) => (
                          <span className={responseClass(consent.granted)} key={`${child.id}-${consent.consentTypeCode}`}>
                            {consent.consentTypeName}: {responseLabel(consent.granted)}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="family-review-subsection">
                      <div className="family-review-subtitle">Agreements</div>
                      <div className="family-review-pill-row">
                        {agreements.map((agreement) => (
                          <span className={responseClass(agreement.accepted)} key={`${child.id}-${agreement.agreementCode}`}>
                            {agreement.title}: {responseLabel(agreement.accepted)}
                          </span>
                        ))}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
        </section>
      </div>
    );
  };

  const hasPrimaryContact =
    Boolean(familyRegistration.primaryContact.firstName.trim()) &&
    Boolean(familyRegistration.primaryContact.lastName.trim());

  const hasPrimaryContactDetails =
    Boolean(familyRegistration.primaryContact.mobileNumber?.trim()) ||
    Boolean(familyRegistration.primaryContact.email?.trim());

  const hasPrimaryContactAddress = Boolean(
    familyRegistration.household.partyAddress?.address ||
      familyRegistration.household.household,
  );
  const hasSecondaryContact = hasPersonDetails(
    familyRegistration.secondaryContact,
  );
  const hasSecondaryContactName =
    Boolean(familyRegistration.secondaryContact.firstName.trim()) &&
    Boolean(familyRegistration.secondaryContact.lastName.trim());

  const isEditingFoundPersonOnly =
    !editingHouseholdId &&
    Boolean(familyRegistration.primaryContact.partyId) &&
    familyRegistration.householdMembers.length === 0;
  const hasRequiredEmergencyContacts =
    !isComponentRequired("EmergencyContact") ||
    familyRegistration.householdMembers.every((member) =>
      hasCompleteEmergencyContact(member.emergencyContact),
    );

  const canSaveFamily =
    hasPrimaryContact &&
    (!isComponentRequired("ContactDetails") || hasPrimaryContactDetails) &&
    (isEditingFoundPersonOnly ||
      !isComponentRequired("Address") ||
      hasPrimaryContactAddress) &&
    (!hasSecondaryContact || hasSecondaryContactName) &&
    hasRequiredEmergencyContacts &&
    !saving &&
    !loadingFamilyContext &&
    !loadingRegistrationRequirements;

  const isEditingFamily = editingHouseholdId !== null;
  const hasCompletedPreviousSteps =
    (activeStep !== 0 ||
      (hasPrimaryContact &&
        (!isComponentRequired("ContactDetails") || hasPrimaryContactDetails) &&
        (!isComponentRequired("Address") || hasPrimaryContactAddress))) &&
    (activeStep !== 1 || !hasSecondaryContact || hasSecondaryContactName) &&
    (activeStep !== 2 || hasRequiredEmergencyContacts);
  const canGoNext = isEditingFamily || hasCompletedPreviousSteps;

  const canGoToStep = (stepIndex: number) =>
    !saving &&
    !loadingFamilyContext &&
    !loadingRegistrationRequirements &&
    (isEditingFamily ||
      ((stepIndex === 0 ||
        (hasPrimaryContact &&
          (!isComponentRequired("ContactDetails") || hasPrimaryContactDetails) &&
          (!isComponentRequired("Address") || hasPrimaryContactAddress))) &&
        (stepIndex <= 1 || !hasSecondaryContact || hasSecondaryContactName) &&
        (stepIndex <= 2 || hasRequiredEmergencyContacts)));

  const goToStep = (stepIndex: number) => {
    if (!canGoToStep(stepIndex)) return;
    setActiveStep(stepIndex);
  };

  return (
    <div className="family-workspace-container">
      {isReviewing && (
        <div className="family-review-toolbar">
          <div className="family-review-tab">Family details</div>
          {isReviewing && !partyContext.readOnly && (
            <button
              type="button"
              className="action-button"
              onClick={() => {
                setIsReviewing(false);
                setActiveStep(selectedMemberId ? 2 : 0);
              }}
              disabled={loadingFamilyContext || loadingRegistrationRequirements}
            >
              Edit
            </button>
          )}
        </div>

      )}
    <div className={`family-workspace${!isReviewing ? " family-workspace--editing" : ""}`}>
      {!isReviewing && (
        <Box sx={{ width: "100%", mb: 1 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label, index) => (
              <Step key={label}>
                <StepButton
                  onClick={() => goToStep(index)}
                  disabled={!canGoToStep(index)}
                  aria-current={activeStep === index ? "step" : undefined}
                  sx={{
                    color: canGoToStep(index) ? "inherit" : "#9ca3af",
                    cursor: canGoToStep(index) ? "pointer" : "default",
                    "& .MuiStepLabel-label": { color: "inherit" },
                  }}
                >
                  {label}
                </StepButton>
              </Step>
            ))}
          </Stepper>
        </Box>
      )}

      {loadingFamilyContext ? (
        <div className="card">
          <div className="card-header">
            <div className="card-title">Loading family context...</div>
          </div>
        </div>
      ) : (
        renderStep()
      )}

      {!isReviewing && <div className={`family-form-actions${activeStep === 2 ? " family-form-actions--children" : ""}`}>
        <div className="family-form-action-group">
        <button
          type="button"
          className="action-button secondary family-step-button"
          aria-label="Back"
          title="Back"
          onClick={() => setActiveStep((prev) => Math.max(prev - 1, 0))}
          disabled={activeStep === 0 || saving || loadingFamilyContext}
        >
          <ArrowBackIcon fontSize="small" />
          <span className="family-step-label">Back</span>
        </button>

        {activeStep < steps.length - 1 && (
          <button
            type="button"
            className="action-button family-step-button"
            aria-label="Next"
            title="Next"
            onClick={() => setActiveStep((prev) => prev + 1)}
            disabled={!canGoNext || saving || loadingFamilyContext}
          >
            <span className="family-step-label">Next</span>
            <ArrowForwardIcon fontSize="small" />
          </button>
        )}

        </div>
        <div className="family-form-action-group">
        {activeStep === 2 && (
          <button type="button" className="action-button family-child-action" disabled={saving || loadingFamilyContext || loadingRegistrationRequirements}
            onClick={isAddingMember ? addHouseholdMember : startAddingChild}>
            {isAddingMember || familyRegistration.householdMembers.length === 0 ? "Add Child" : "Add Another Child"}
          </button>
        )}
        {activeStep === steps.length - 1 && <button
          type="button"
          className="form-submit-button"
          onClick={saveFamily}
          disabled={!canSaveFamily}
          title={
            canSaveFamily
              ? undefined
              : "Complete the required primary contact, contact details and address fields before saving a family."
          }
        >
          {saveLabel}
        </button>}
        </div>
      </div>}
    </div>
    </div>
  );
}
